// src/pages/LoginPage.jsx
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import logo from '../assets/logo.jpeg';
import { loginUser } from '../api';

const palette = {
  ink: '#1E2A38',
  inkDark: '#172033',
  inkLight: '#2C3A4B',
  accent: '#2B6CB0',
  bg: '#F5F7FB',
  panel: '#FFFFFF',
  text: '#1F2937',
  subtext: '#6B7280',
  border: '#E5E7EB',
  error: '#E11D48',
};

// Demo accounts
const DEMO_ACCOUNTS = {
  emily: {
    email: 'emily@mywrapmarket.com',
    password: 'V7h$9mN2!wr@PM',
  },
  sierra: {
    // Sierra Marie (customer demo)
    email: 'sierra.marie@tester.com',        // <-- keep this as your current demo email
    // You can also accept the old one if needed, see isSierraEmail() below.
    password: 'S1err@Wrap!2025',
  },
};

// Helper: normalize + detect Sierra emails (accept both current & legacy)
const isSierraEmail = (email = '') => {
  const e = String(email).trim().toLowerCase();
  return e === DEMO_ACCOUNTS.sierra.email.toLowerCase()
      || e === 'sierra.marie@wrapmarket.com'; // legacy alias (optional)
};

export default function LoginPage() {
  const navigate = useNavigate();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError]     = useState('');
  const [loading, setLoading] = useState(false);

  const handleLogin = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await loginUser(username.trim(), password);
      const data = response?.data || response || {};

      // Prefer email if present; fall back to username; otherwise use input
      const effectiveUser = (data.email || data.username || username).trim();
      const effectiveEmailLower = effectiveUser.toLowerCase();

      if (data?.token) {
        localStorage.setItem('token', data.token);
        localStorage.setItem('username', effectiveUser);
        localStorage.setItem('userId', String(data.id ?? ''));
        // Preserve server role if provided; else leave blank for staff demo, but set for Sierra below
        if (data.role != null) localStorage.setItem('role', String(data.role));
      }

      // ⭐ Sierra handling → ensure customer role flag(s)
      if (isSierraEmail(effectiveEmailLower)) {
        localStorage.setItem('wrapmarket_role', 'customer');
        // If API didn't include a usable role, force a friendly string for the client
        const currentRole = (localStorage.getItem('role') || '').toLowerCase();
        if (!currentRole || currentRole === '3' || currentRole === 'user') {
          localStorage.setItem('role', 'customer');
        }
      }

      navigate('/dashboard');
    } catch (err) {
      // ---- Client-side demo fallback for Sierra ----
      const inputEmail = username.trim().toLowerCase();
      const inputPass  = password;
      if (isSierraEmail(inputEmail) && inputPass === DEMO_ACCOUNTS.sierra.password) {
        // Mock a token & set required fields so Dashboard works
        const token = 'demo-sierra-' + Date.now();
        localStorage.setItem('token', token);
        localStorage.setItem('username', DEMO_ACCOUNTS.sierra.email);
        localStorage.setItem('userId', 'demo-sierra');
        localStorage.setItem('role', 'customer');
        localStorage.setItem('wrapmarket_role', 'customer');
        setLoading(false);
        navigate('/dashboard');
        return;
      }

      setError(err?.message || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  const inputStyle = {
    width: '100%',
    marginTop: 6,
    marginBottom: 16,
    padding: '10px 12px',
    border: `1px solid ${palette.border}`,
    borderRadius: 10,
    fontSize: 14,
    background: '#fff',
    outline: 'none',
    transition: 'box-shadow .15s ease, border-color .15s ease',
  };

  const inputFocus = (e) => {
    e.currentTarget.style.borderColor = palette.accent;
    e.currentTarget.style.boxShadow = `0 0 0 4px rgba(43,108,176,.12)`;
  };
  const inputBlur = (e) => {
    e.currentTarget.style.borderColor = palette.border;
    e.currentTarget.style.boxShadow = 'none';
  };

  // Quick-fill helpers for demos (optional)
  const useDemo = (which) => {
    const acc = DEMO_ACCOUNTS[which];
    if (!acc) return;
    setUsername(acc.email);
    setPassword(acc.password);
  };

  return (
    <div
      style={{
        display: 'flex',
        minHeight: '100vh',
        background: palette.bg,
      }}
    >
      {/* Brand side */}
      <div
        style={{
          flex: '1 1 50%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background:
            'linear-gradient(135deg, #0F172A 0%, #1F2937 60%, #111827 100%)',
          padding: 24,
        }}
      >
        <div
          style={{
            background: 'rgba(255,255,255,.06)',
            border: '1px solid rgba(255,255,255,.08)',
            borderRadius: 16,
            padding: 28,
            backdropFilter: 'blur(2px)',
            boxShadow: '0 20px 40px rgba(0,0,0,.25)',
          }}
        >
          <img
            src={logo}
            alt="The Wrap Market"
            style={{
              maxWidth: 360,
              height: 'auto',
              display: 'block',
              filter: 'drop-shadow(0 2px 2px rgba(0,0,0,.35))',
            }}
          />
        </div>
      </div>

      {/* Form side */}
      <div
        style={{
          flex: '1 1 50%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 24,
        }}
      >
        <div
          style={{
            width: '100%',
            maxWidth: 420,
            background: palette.panel,
            border: `1px solid ${palette.border}`,
            boxShadow: '0 16px 40px rgba(0,0,0,.06)',
            borderRadius: 16,
            padding: 28,
          }}
        >
          <h2
            style={{
              fontSize: 22,
              fontWeight: 800,
              color: palette.ink,
              margin: 0,
              marginBottom: 6,
              letterSpacing: '.2px',
            }}
          >
            Welcome back
          </h2>
          <p style={{ fontSize: 13, color: palette.subtext, marginTop: 0, marginBottom: 20 }}>
            Sign in to continue to your dashboard
          </p>

          <label style={{ fontSize: 12, fontWeight: 700, color: palette.inkLight }}>
            Email
          </label>
          <input
            type="text"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            placeholder="e.g. emily@mywrapmarket.com"
            style={inputStyle}
            onFocus={inputFocus}
            onBlur={inputBlur}
            autoComplete="username"
          />

          <label style={{ fontSize: 12, fontWeight: 700, color: palette.inkLight }}>
            Password
          </label>
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Enter password"
            style={inputStyle}
            onFocus={inputFocus}
            onBlur={inputBlur}
            autoComplete="current-password"
            onKeyDown={(e) => { if (e.key === 'Enter') handleLogin(); }}
          />

          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              marginBottom: 14,
              justifyContent: 'space-between',
            }}
          >
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: palette.subtext }}>
              <input type="checkbox" id="remember" />
              Remember me
            </label>
            <button
              type="button"
              onClick={() => alert('Forgot password flow')}
              style={{
                fontSize: 12,
                color: palette.accent,
                background: 'transparent',
                border: 'none',
                cursor: 'pointer',
                padding: 0,
              }}
            >
              Forgot password?
            </button>
          </div>

          {error && (
            <div
              style={{
                color: palette.error,
                fontSize: 12,
                marginBottom: 10,
                background: '#FDF2F8',
                border: '1px solid #FBCFE8',
                borderRadius: 10,
                padding: '8px 10px',
              }}
            >
              {error}
            </div>
          )}

          <button
            onClick={handleLogin}
            disabled={loading}
            style={{
              background: palette.ink,
              color: '#fff',
              padding: '12px 14px',
              width: '100%',
              borderRadius: 10,
              fontSize: 14,
              fontWeight: 700,
              cursor: 'pointer',
              border: 'none',
              transition: 'background .15s ease, transform .05s ease',
              boxShadow: '0 6px 16px rgba(30,42,56,.20)',
              opacity: loading ? 0.9 : 1,
            }}
            onMouseDown={(e) => (e.currentTarget.style.transform = 'scale(0.99)')}
            onMouseUp={(e) => (e.currentTarget.style.transform = 'scale(1)')}
            onMouseEnter={(e) => (e.currentTarget.style.background = palette.inkDark)}
            onMouseLeave={(e) => (e.currentTarget.style.background = palette.ink)}
          >
            {loading ? 'Signing in…' : 'Sign In'}
          </button>

          {/* Demo helpers */}
          <div style={{ fontSize: 11, color: '#9CA3AF', marginTop: 14, lineHeight: 1.6 }}>
            Demo 1 (staff): {DEMO_ACCOUNTS.emily.email} / {DEMO_ACCOUNTS.emily.password}
            <button
              type="button"
              onClick={() => useDemo('emily')}
              style={{ marginLeft: 8, fontSize: 11, color: palette.accent, background: 'transparent', border: 'none', cursor: 'pointer' }}
            >
              Autofill
            </button>
            <br />
            Demo 2 (customer – Sierra Marie): {DEMO_ACCOUNTS.sierra.email} / {DEMO_ACCOUNTS.sierra.password}
            <button
              type="button"
              onClick={() => useDemo('sierra')}
              style={{ marginLeft: 8, fontSize: 11, color: palette.accent, background: 'transparent', border: 'none', cursor: 'pointer' }}
            >
              Autofill
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
